<div class="flex flex-col">
  <div class="flex flex-col lg:flex-row gap-4 min-h-0 w-full flex-1">
    <div class="flex flex-col lg:w-1/2 w-full gap-4">
      <!-- Code Viewer -->
      <div class="h-fit w-full flex flex-col p-6 card">
        <div class="flex justify-between items-center mb-4">
          <p class="text-lg font-semibold">Code View</p>
          <div class="flex items-center gap-4">
            <!-- Clarity Recordings -->
            <app-button
              class="tooltip tooltip-primary"
              data-tip="Clarity will take at least 1 hour to process after the candidate submits the test!"
              [color]="'secondary'"
              [size]="'medium'"
              [label]="'Recordings'"
              (clicked)="openClarityRecordings()" />
            <!-- Clarity Dashboard -->
            <app-button
              [color]="'primary'"
              [size]="'medium'"
              [label]="'Open in Clarity'"
              (clicked)="openClarityDashboard()" />
          </div>
        </div>

        <div
          class="h-75 w-full lg:h-100 items-center justify-center rounded-lg overflow-hidden">
          <app-code-editor
            [language]="codeLanguage()"
            [codeInput]="candidateCode()"
            [readOnly]="true" />
        </div>
      </div>

      <!-- Test Case Results -->
      <div class="w-full card p-6 max-h-full overflow-hidden">
        <p class="font-semibold text-lg mb-4">Test Cases</p>
        @if( testCaseResult().length > 0){

        <div class="flex flex-row h-full w-full">
          <div class="flex-col w-full h-full">
            @for (problem of selectedProblem()?.testCaseResults ; track
            problem.testCaseResultId) {
            <div class="my-2">
              <app-view-test-case [data]="problem" [index]="$index + 1" />
            </div>
            }
          </div>
        </div>
        }@else{
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-center h-full">
            <div class="text-gray-500">No test case found.</div>
          </div>
        </div>
        }
      </div>
    </div>

    <!-- Candidate Info and Problems -->
    <div class="flex-1 min-w-0 gap-4 flex flex-col">
      <!-- Loading State -->
      @if (isAnalyzeLoading()) {
      <div class="flex items-center justify-center h-full">
        <span class="loading loading-dots loading-lg"></span>
      </div>
      }

      <!-- Data Available -->
      @else if (analyzeData()) {
      <!-- Candidate Info -->
      <div class="flex card p-6">
        <!-- Desktop Layout -->
        <div class="hidden md:flex justify-between flex-row items-center">
          <div>
            <h2 class="lg:text-2xl text-xl font-semibold">
              {{ analyzeData()?.email || 'Unknown Candidate' }}
            </h2>
            <div class="flex lg:flex-row flex-col gap-1 mt-1">
              <p class="lg:text-lg text-gray-500">
                {{ submission()?.title || 'Unknown Test Set' }}&nbsp;
              </p>
              <app-badge [value]="submission()?.difficulty || 'Unknown'" />
            </div>
          </div>
          <div class="flex flex-col items-center" data-testid="overall-score">
            <span class="text-gray-500">Overall Score</span>
            <span class="text-4xl font-bold"
              >{{ submission()?.totalScore ? submission()?.totalScore + '%' :
              '0%' }}</span
            >
          </div>
        </div>

        <!-- Mobile Layout -->
        <div class="flex md:hidden flex-col items-center text-center space-y-4">
          <h2 class="text-xl font-semibold">
            {{ analyzeData()?.email || 'Unknown Candidate' }}
          </h2>
          <div class="flex flex-col items-center gap-2">
            <p class="text-gray-500">
              {{ submission()?.title || 'Unknown Test Set' }}
            </p>
            <app-badge [value]="submission()?.difficulty || 'Unknown'" />
          </div>
          <div class="flex flex-col items-center">
            <span class="text-gray-500">Overall Score</span>
            <span class="text-4xl font-bold"
              >{{ submission()?.totalScore ? submission()?.totalScore + '%' :
              '0%' }}</span
            >
          </div>
        </div>
      </div>

      <!-- Problems List -->
      <div class="card p-6 flex-col">
        <p class="text-lg font-semibold mb-4">
          Select a problem to view the candidate's code and performance.
        </p>

        @if (problems().length === 0) {
        <div class="flex items-center justify-center h-32">
          <div class="text-gray-500">No problems found.</div>
        </div>
        } @else {
        <div class="space-y-4 overflow-y-auto">
          <!-- Problem Cards -->
          @for (problem of problems(); track problem.problemId) {
          <div
            class="border shadow-sm rounded-lg cursor-pointer transition-all hover:shadow-md hover:border-primary-lighter"
            [class.border-primary]="selectedProblem()?.problemId === problem.problemId"
            [class.bg-blue-50]="selectedProblem()?.problemId === problem.problemId"
            tabindex="0"
            role="button"
            (click)="selectProblem(problem)"
            data-testid="problem-submission-button">
            <div class="p-6">
              <!-- Desktop Layout -->
              <div class="hidden md:flex items-center gap-4">
                <div class="space-y-3 w-[60%]">
                  @if(problem.status === 'Submitted'){
                  <app-badge
                    [value]="problem.language || 'Unknown'"
                    [size]="'sm'" />
                  }
                  <div>
                    <h3 class="text-xl font-bold text-gray-900">
                      {{ problem.title || 'Unknown Problem' }}
                    </h3>
                    @if(problem.status === 'Submitted'){
                    <p class="text-sm text-gray-600">
                      Time used: {{ formatTime(problem.timeUse,
                      problem.duration) }}
                    </p>
                    } @else {
                    <span
                      class="text-sm font-bold text-error-lighter text-center"
                      >{{problem.status}}</span
                    >
                    }
                  </div>
                </div>
                <div class="flex justify-center w-[15%]">
                  <app-badge [value]="problem.difficulty" />
                </div>
                <div class="flex flex-col items-center w-[25%]">
                  <span class="text-sm text-gray-500 mb-1">Score</span>
                  <span class="text-4xl font-bold"
                    >{{ problem.score ? problem.score + '%' : '0%' }}</span
                  >
                </div>
              </div>

              <!-- Mobile Layout -->
              <div
                class="flex md:hidden flex-col items-center text-center space-y-4">
                @if(problem.status === 'Submitted'){
                <app-badge
                  [value]="problem.language || 'Unknown'"
                  [size]="'sm'" />
                }

                <div>
                  <h3 class="text-xl font-bold text-gray-900">
                    {{ problem.title }}
                  </h3>
                  @if(problem.status === 'Submitted'){
                  <p class="mt-2 text-sm text-gray-600">
                    Time used: {{ formatTime(problem.timeUse, problem.duration)
                    }}
                  </p>
                  } @else {
                  <span class="text-sm font-bold text-error-lighter text-center"
                    >{{problem.status}}</span
                  >
                  }
                </div>
                <app-badge [value]="problem.difficulty" />
                <div class="flex flex-col items-center">
                  <span class="text-sm text-gray-500 mb-1">Score</span>
                  <span class="text-4xl font-bold"
                    >{{ problem.score ? problem.score + '%' : '0%' }}</span
                  >
                </div>
              </div>

              <!-- Selected indicator -->
              @if (selectedProblem()?.problemId === problem.problemId) {
              <div class="mt-2 flex items-center justify-center">
                <div class="flex items-center text-primary text-sm font-medium">
                  <svg
                    class="w-4 h-4 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"></path>
                  </svg>
                  Currently viewing
                </div>
              </div>
              }
            </div>
          </div>
          }
        </div>
        }
      </div>
      }

      <!-- No Data State -->
      @else {
      <div class="flex items-center justify-center h-full">
        <div class="text-gray-500">No data found.</div>
      </div>
      }
    </div>
  </div>
</div>
